/**
 * SagaDemoPanel — C067 Saga 트랜잭션 데모 (queue #646 §R646-2 항목2)
 * 고정 3단계 데모 saga(reserve → charge → confirm) 를 failAtStep 으로 선택 실행.
 * 격리 write — saga 이름 "showcase-demo-saga" 로 격리된 데모 인스턴스만 적재.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSagaRun } from '../../hooks/useSagaDemo';
import {
  cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, monoStyle,
  primaryButtonStyle, resultBoxStyle, DemoSafetyBanner, DemoErrorState, DemoEmptyHint,
} from './demoShared';

const FAIL_OPTIONS: Array<0 | 1 | 2 | 3> = [0, 1, 2, 3];

export function SagaDemoPanel() {
  const { t } = useTranslation();
  const sagaMutation = useSagaRun();
  const [failAtStep, setFailAtStep] = useState<0 | 1 | 2 | 3>(0);

  return (
    <section aria-labelledby="saga-panel-title">
      <h2 id="saga-panel-title" style={sectionTitleStyle}>{t('showcase.demo.saga.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.saga.subtitle')}</p>

      <DemoSafetyBanner text={t('showcase.demo.saga.banner')} />

      <div style={cardStyle}>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label htmlFor="saga-fail-at" style={labelStyle}>{t('showcase.demo.saga.failAtStepLabel')}</label>
          <select
            id="saga-fail-at"
            value={failAtStep}
            onChange={(e) => setFailAtStep(Number(e.target.value) as 0 | 1 | 2 | 3)}
            style={inputStyle}
          >
            {FAIL_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? t('showcase.demo.saga.failAtStepAllSuccess') : t('showcase.demo.saga.failAtStepN', { n })}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => sagaMutation.mutate({ failAtStep })}
          disabled={sagaMutation.isPending}
          aria-busy={sagaMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: sagaMutation.isPending ? 0.6 : 1 }}
        >
          {sagaMutation.isPending ? t('showcase.demo.saga.running') : t('showcase.demo.saga.runButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {sagaMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={sagaMutation.error} />
          )}
          {!sagaMutation.isError && !sagaMutation.data && <DemoEmptyHint text={t('showcase.demo.saga.empty')} />}
          {sagaMutation.data && !sagaMutation.isError && (
            <div role="region" aria-label={t('showcase.demo.saga.resultLabel')} style={resultBoxStyle}>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-2) var(--spacing-4)', margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-sm)' }}>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.saga.statusLabel')}</dt>
                <dd style={{ margin: 0, fontWeight: 700, color: sagaMutation.data.status === 'COMPLETED' ? 'var(--color-status-success)' : 'var(--color-status-error)' }}>
                  {sagaMutation.data.status}
                </dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.saga.sagaNameLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{sagaMutation.data.sagaName}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.saga.instanceIdLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{sagaMutation.data.instanceId ?? '—'}</dd>
              </dl>

              {/* ★NON_NULL 생략 필드는 undefined — `!= null` 로 체크(!== null 금지) */}
              {sagaMutation.data.failedStep != null && (
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-status-error)', marginBottom: 'var(--spacing-2)' }}>
                  {t('showcase.demo.saga.failedStepLabel')}: <code style={monoStyle}>{sagaMutation.data.failedStep}</code>
                </p>
              )}

              {sagaMutation.data.compensatedSteps.length > 0 && (
                <>
                  <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--spacing-1)' }}>
                    {t('showcase.demo.saga.compensatedLabel')}
                  </p>
                  <ul style={{ margin: '0 0 var(--spacing-2) 0', paddingLeft: 'var(--spacing-4)', fontSize: 'var(--font-size-xs)', ...monoStyle }}>
                    {sagaMutation.data.compensatedSteps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ul>
                </>
              )}

              {sagaMutation.data.failureMessage != null && (
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-status-error)', margin: 0 }}>
                  {sagaMutation.data.failureMessage}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
